import React, { useEffect, useRef, useState } from 'react'

import './_taxDocument.scss'
import SignatureCanvas from 'react-signature-canvas'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import colors from 'styles/colors'

import { getW9taxform, w9taxform } from './taxformapi'

interface FormData {
    individualName: string
    businessName: string
    taxClassification: string
    llcType: string
    limitedLiabilityType: string
    exemptFromFatca: string
    hasForeignPartners: boolean
    exemptPayeeCode: string
    streetAddress: string
    city: string
    state: string
    zipCode: string
    requesterFirstName: string
    requesterLastName: string
    requesterStreet: string
    requesterCity: string
    requesterState: string
    requesterZip: string
    accountNumber: any
    [key: `ssn${number}`]: string  
    [key: `ein${number}`]: string  
    signature: string
}

function W9() {
    const [formData, setFormData] = useState<FormData>({
        individualName: '',
        businessName: '',
        taxClassification: '',
        llcType: '',
        limitedLiabilityType: '',
        exemptFromFatca: '',
        hasForeignPartners: false,
        exemptPayeeCode: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        requesterFirstName: '',
        requesterLastName: '',
        requesterStreet: '',
        requesterCity: '',
        requesterState: '',
        requesterZip: '',
        accountNumber: '',
        ssn0: '',
        ssn1: '',
        ssn2: '',
        ssn3: '',
        ssn4: '',
        ssn5: '',
        ssn6: '',
        ssn7: '',
        ssn8: '',
        ein0: '',
        ein1: '',
        ein2: '',
        ein3: '',
        ein4: '',
        ein5: '',
        ein6: '',
        ein7: '',
        ein8: '',
        signature: ''
    })

    // State to store validation errors
    const [errors, setErrors] = useState<
        Partial<Record<keyof FormData, string>>
    >({})
    const ssnRefs = useRef<(HTMLInputElement | null)[]>(Array(9).fill(null))
    const einRefs = useRef<(HTMLInputElement | null)[]>(Array(9).fill(null))
    useEffect(() => {
        const fetchData = async () => {
            const result = await getW9taxform()
            if (result && result.success) {
                const { data: { data: taxData } = {} } = result
                if (result.success) {
                    setFormData({
                        individualName: taxData?.individualName || '',
                        businessName: taxData?.businessName || '',
                        taxClassification: taxData?.taxClassification || '',
                        llcType: taxData?.llcType || '',
                        limitedLiabilityType:
                            taxData?.limitedLiabilityType || '',
                        exemptFromFatca: taxData?.exemptFromFatca || '',
                        hasForeignPartners:
                            taxData?.hasForeignPartners || false,
                        exemptPayeeCode: taxData?.exemptPayeeCode || '',
                        streetAddress: taxData?.streetAddress || '',
                        city: taxData?.city || '',
                        state: taxData?.state || '',
                        zipCode: taxData?.zipCode || '',
                        requesterFirstName: taxData?.requesterFirstName || '',
                        requesterLastName: taxData?.requesterLastName || '',
                        requesterStreet: taxData?.requesterStreet || '',
                        requesterCity: taxData?.requesterCity || '',
                        requesterState: taxData?.requesterState || '',
                        requesterZip: taxData?.requesterZip || '',
                        accountNumber: taxData?.accountNumber || '',
                        ssn0: taxData?.ssn?.slice(0, 1) || '',
                        ssn1: taxData?.ssn?.slice(1, 2) || '',
                        ssn2: taxData?.ssn?.slice(2, 3) || '',
                        ssn3: taxData?.ssn?.slice(3, 4) || '',
                        ssn4: taxData?.ssn?.slice(4, 5) || '',
                        ssn5: taxData?.ssn?.slice(5, 6) || '',
                        ssn6: taxData?.ssn?.slice(6, 7) || '',
                        ssn7: taxData?.ssn?.slice(7, 8) || '',
                        ssn8: taxData?.ssn?.slice(8, 9) || '',
                        ein0: taxData?.ein?.slice(0, 1) || '',
                        ein1: taxData?.ein?.slice(1, 2) || '',
                        ein2: taxData?.ein?.slice(2, 3) || '',
                        ein3: taxData?.ein?.slice(3, 4) || '',
                        ein4: taxData?.ein?.slice(4, 5) || '',
                        ein5: taxData?.ein?.slice(5, 6) || '',
                        ein6: taxData?.ein?.slice(6, 7) || '',
                        ein7: taxData?.ein?.slice(7, 8) || '',
                        ein8: taxData?.ein?.slice(8, 9) || '',
                        signature: taxData?.signature || ''
                    })
                } else {
                    console.log(' W-9 Form Fetch Error')
                }
            }
        }
        fetchData()
    }, [])

    
    const validateForm = (): Partial<Record<keyof FormData, string>> => {
        const newErrors: Partial<Record<keyof FormData, string>> = {}

        
        if (!formData.individualName.trim()) {
            newErrors.individualName = 'Name is required'
        }
        if (!formData.businessName.trim()) {
            newErrors.businessName = 'Business name is required'
        }
        if (!formData.taxClassification.trim()) {
            newErrors.taxClassification = 'Tax classification is required'
        }
        if (!formData.limitedLiabilityType.trim()) {
            newErrors.limitedLiabilityType =
                'Limited liability type is required'
        }
        if (
            !formData.streetAddress.trim() &&
            !formData.requesterStreet.trim()
        ) {
            newErrors.streetAddress = 'Street address is required'
        }
        if (!formData.requesterFirstName.trim()) {
            newErrors.requesterFirstName = 'Requester’s first name is required'
        }
        if (!formData.requesterLastName.trim()) {
            newErrors.requesterLastName = 'Requester’s last name is required'
        }
        if (!formData.requesterCity.trim()) {
            newErrors.requesterCity = 'Requester’s city is required'
        }
        if (!formData.requesterState.trim()) {
            newErrors.requesterState = 'Requester’s state is required'
        }
        if (!formData.requesterZip.trim()) {
            newErrors.requesterZip = 'Requester’s ZIP code is required'
        }
        if (!formData.city.trim()) {
            newErrors.city = 'City is required'
        }
        if (!formData.state.trim()) {
            newErrors.state = 'State is required'
        }
        if (!formData.accountNumber.trim()) {
            newErrors.accountNumber = 'Account numbers are required'
        }

         
        const zip = String(formData.zipCode).trim()
        if (!zip) {
            newErrors.zipCode = 'ZIP code is required'
            console.log('Validation Error: ZIP code is required')
        } else if (!/^\d{5}(-\d{4})?$/.test(zip)) {
            newErrors.zipCode =
                'Invalid ZIP code format (e.g., 12345 or 12345-6789)'
        }

        // Requester ZIP code validation (optional, but if provided, must be valid)
        const requesterZip = String(formData.requesterZip).trim()
        if (requesterZip && !/^\d{5}(-\d{4})?$/.test(requesterZip)) {
            newErrors.requesterZip =
                'Invalid ZIP code format (e.g., 12345 or 12345-6789)'
        }

        // SSN and EIN validation
        const ssn = `${formData.ssn0}${formData.ssn1}${formData.ssn2}${formData.ssn3}${formData.ssn4}${formData.ssn5}${formData.ssn6}${formData.ssn7}${formData.ssn8}`
        const ein = `${formData.ein0}${formData.ein1}${formData.ein2}${formData.ein3}${formData.ein4}${formData.ein5}${formData.ein6}${formData.ein7}${formData.ein8}`
        if (ssn && ein) {
            newErrors.ssn0 = 'Provide either SSN or EIN, not both'
            newErrors.ein0 = 'Provide either SSN or EIN, not both'
        } else if (!ssn && !ein) {
            newErrors.ssn0 = 'SSN or EIN is required'
            newErrors.ein0 = 'SSN or EIN is required'
        } else if (ssn && !/^\d{9}$/.test(ssn)) {
            newErrors.ssn0 = 'SSN must be 9 digits'
        } else if (ein && !/^\d{9}$/.test(ein)) {
            newErrors.ein0 = 'EIN must be 9 digits'
        }

        // Signature validation
        if (!formData.signature) {
            newErrors.signature = 'Signature is required'
        }

        return newErrors
    }

    const validateField = (
        name: keyof FormData,
        value: any
    ): string | undefined => {
        let error: string | undefined

        // Required fields
        if (
            [
                'individualName',
                'businessName',
                'taxClassification',
                'limitedLiabilityType',
                'streetAddress',
                'city',
                'state',
                'requesterFirstName',
                'requesterLastName',
                'requesterCity',
                'requesterState',
                'requesterZip',
                'accountNumber',
                'signature',
            ].includes(name) &&
            !value.trim()
        ) {
            error = `${name.replace(/([A-Z])/g, ' $1').trim()} is required`
        }

        // ZIP code validation
        if (name === 'zipCode') {
            const zip = String(value).trim()
            if (!zip) {
                error = 'ZIP code is required'
            } else if (!/^\d{5}(-\d{4})?$/.test(zip)) {
                error = 'Invalid ZIP code format (e.g., 12345 or 12345-6789)'
            }
        }

        // Requester ZIP code validation
        if (name === 'requesterZip') {
            const zip = String(value).trim()
            if (zip && !/^\d{5}(-\d{4})?$/.test(zip)) {
                error = 'Invalid ZIP code format (e.g., 12345 or 12345-6789)'
            }
        }

         
        if (name.startsWith('ssn') || name.startsWith('ein')) {
            const ssn = `${formData.ssn0}${formData.ssn1}${formData.ssn2}${formData.ssn3}${formData.ssn4}${formData.ssn5}${formData.ssn6}${formData.ssn7}${formData.ssn8}`
            const ein = `${formData.ein0}${formData.ein1}${formData.ein2}${formData.ein3}${formData.ein4}${formData.ein5}${formData.ein6}${formData.ein7}${formData.ein8}`

            
            if (
                name === 'ssn8' ||
                name === 'ein8' ||
                (name.startsWith('ssn') && ein.length > 0) ||
                (name.startsWith('ein') && ssn.length > 0)
            ) {
                if (ssn && ein) {
                    error = 'Provide either SSN or EIN, not both'
                    addToast('error', error)
                } else if (!ssn && !ein) {
                    error = 'SSN or EIN is required'
                    addToast('error', error)
                } else if (ssn && ssn.length < 9) {
                    error = 'All SSN fields must be filled'
                    addToast('error', error)
                } else if (ein && ein.length < 9) {
                    error = 'All EIN fields must be filled'
                    addToast('error', error)
                } else if (ssn && !/^\d{9}$/.test(ssn)) {
                    error = 'SSN must be 9 digits'
                    addToast('error', error)
                } else if (ein && !/^\d{9}$/.test(ein)) {
                    error = 'EIN must be 9 digits'
                    addToast('error', error)
                }
            }
        }
 
        if (name === 'signature' && !value) {
            error = 'Signature is required'
            addToast('error', error)
        }

        return error
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target

        // Sanitize ZIP code and Requester ZIP code
        if (name === 'zipCode' || name === 'requesterZip') {
            if (/^\d{0,5}(-\d{0,4})?$/.test(value) && value.length <= 10) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }))
            }
        }
          
        else if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }))
        }
         
        else if (name.startsWith('ssn') || name.startsWith('ein')) {
            if (value.length <= 1 && /^[0-9]*$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }))
            }
        }
 
        else if (name === 'limitedLiabilityType') {
            if (value.length <= 1 && /^[CSPcsp]*$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value.toUpperCase()
                }))
            }
        }
         
        else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }))
        }
    }

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
        type: 'ssn' | 'ein'
    ) => {
        const { name, value } = e.target
 
        if (value.length === 1 && /^[0-9]$/.test(value)) {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                
                ...(type === 'ssn' && index < 8
                    ? { [`ssn${index + 1}`]: '' }
                    : {}),
                ...(type === 'ein' && index < 8
                    ? { [`ein${index + 1}`]: '' }
                    : {})
            }))
        } else if (value.length <= 1 && /^[0-9]*$/.test(value)) {
            
            setFormData(prev => ({
                ...prev,
                [name]: value
            }))
        }

        // Move focus to next field if a digit is entered
        if (value.length === 1 && /^[0-9]$/.test(value)) {
            if (type === 'ssn' && index < 8 && ssnRefs.current[index + 1]) {
                ssnRefs.current[index + 1]?.focus()
            } else if (
                type === 'ein' &&
                index < 8 &&
                einRefs.current[index + 1]
            ) {
                einRefs.current[index + 1]?.focus()
            }
        }
    }

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number,
        type: 'ssn' | 'ein'
    ) => {
        if (e.key === 'Backspace') {
            const name = e.currentTarget.name
            const value = e.currentTarget.value
            if (value) {
                setFormData(prev => ({
                    ...prev,
                    [name]: ''
                }))
                e.preventDefault() // Prevent default to avoid double-clearing
            }
             
            else if (index > 0) {
                const prevFieldName =
                    type === 'ssn' ? `ssn${index - 1}` : `ein${index - 1}`
                setFormData(prev => ({
                    ...prev,
                    [prevFieldName]: ''
                }))
                if (type === 'ssn' && ssnRefs.current[index - 1]) {
                    ssnRefs.current[index - 1]?.focus()
                } else if (type === 'ein' && einRefs.current[index - 1]) {
                    einRefs.current[index - 1]?.focus()
                }
                e.preventDefault() // Prevent default to control focus
            }
        }
    }
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        const error = validateField(name as keyof FormData, value)

        setErrors(prev => ({
            ...prev,
            [name]: error
        }))

        // if (error) {
        //     addToast('error', error);
        // }
    }

    // const handleEnd = () => {
    //     if (sigRef.current) {
    //         // console.log('sigRef methods:', Object.getOwnPropertyNames(sigRef.current));
    //         // console.log('getTrimmedCanvas available:', typeof sigRef.current.getTrimmedCanvas);
    //     }
    //     if (!sigRef.current) {
    //         console.error('Signature canvas ref is null')
    //         return
    //     }
    //     try {
    //         const signatureDataUrl = !sigRef.current.isEmpty()
    //             ? sigRef.current.toDataURL('image/png')
    //             : ''
    //         setFormData(prev => ({ ...prev, signature: signatureDataUrl }))

    //         const error = validateField('signature', signatureDataUrl)
    //         setErrors(prev => ({
    //             ...prev,
    //             signature: error
    //         }))
    //         // if (error) {
    //         //     addToast('error', error);
    //         // }
    //     } catch (error) {
    //         console.error('Error capturing signature:', error)
    //     }
    // }

    const handleSubmit = async () => {
        const newErrors = validateForm()
        setErrors(newErrors)

        if (Object.keys(newErrors).length > 0) {
            addToast(
                'error',
                'Please correct the errors in the form before submitting.'
            )
            return
        }

        const ssn = `${formData.ssn0}${formData.ssn1}${formData.ssn2}${formData.ssn3}${formData.ssn4}${formData.ssn5}${formData.ssn6}${formData.ssn7}${formData.ssn8}`
        const ein = `${formData.ein0}${formData.ein1}${formData.ein2}${formData.ein3}${formData.ein4}${formData.ein5}${formData.ein6}${formData.ein7}${formData.ein8}`

        const payload = {
            individualName: formData.individualName,
            businessName: formData.businessName,
            taxClassification: formData.taxClassification,
            llcTaxClass: formData.limitedLiabilityType || '',
            ein: ein || '',
            exemptPayeeCode: formData.exemptPayeeCode || '',
            hasForeignPartners: formData.hasForeignPartners ? 'N' : 'Y',
            ssn: ssn || '',
            accountNumbers: formData.accountNumber || '',
            date: new Date().toISOString().split('T')[0],
            zip: formData.zipCode || '',
            streetAddress: formData.streetAddress || '',
            city: formData.city || '',
            state: formData.state || '',
            requesterFirstName: formData.requesterFirstName || '',
            requesterLastName: formData.requesterLastName || '',
            requesterStreet: formData.requesterStreet || '',
            requesterCity: formData.requesterCity || '',
            requesterState: formData.requesterState || '',
            requesterZip: formData.requesterZip || '',
            exemptFromFatca: formData.exemptFromFatca || '',
            signature: formData.signature,
            limitedLiabilityType: formData.limitedLiabilityType || ''
        }

        try {
            const result = await w9taxform(payload)
            if (result.status === 'OK') {
                addToast('success', 'Form Submitted Successfully')
            }
            setErrors({})
        } catch (error) {
            console.error('Error submitting W9 form:', error)
            addToast('error', 'Failed to submit the form. Please try again.')
        }
    }

    const sigRef = useRef<SignatureCanvas | null>(null)

    // const clearSignature = () => {
    //     if (sigRef.current) {
    //         sigRef.current.clear()
    //         setFormData(prev => ({ ...prev, signature: '' }))
    //         const error = validateField('signature', '')
    //         setErrors(prev => ({
    //             ...prev,
    //             signature: error
    //         }))
    //         // if (error) {
    //         //     addToast('error', error);
    //         // }
    //     }
    // }

    return (
        <div className='tax-document'>
        <div className='mb-5'>
            <div className='mb-6'>
                <h1 className='mb-2'>
                    Request for Taxpayer Identification Number and Certification
                </h1>
                <p className='text-gray-600 mt-2'>
                    Go to{' '}
                    <a
                        href='https://www.irs.gov/FormW9'
                        className='text-blue-600 underline'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        www.irs.gov/FormW9
                    </a>{' '}
                    for instructions and the latest information.
                </p>
            </div>

            <form
                className='w9-form'
                onSubmit={e => {
                    e.preventDefault()
                    handleSubmit()
                }}
            >
                {/* Line 1 */}
                <div className='mb-6'>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'individualName'}>
                            {
                                '1. Name of entity/individual (required)(For a sole proprietor or disregarded entity, enter the owner’s name on line 1, and the business/disregarded entity’s name on line 2.)'
                            }
                        </InputLabel>
                        <Input
                            name='individualName'
                            id={'individualName'}
                            type='text'
                            $filled={true}
                            value={formData.individualName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.individualName ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>
                </div>

                {/* Line 2 */}
                <div className='mb-6'>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'businessName'}>
                            {
                                '2. Business name/disregarded entity name, if different from above.'
                            }
                        </InputLabel>
                        <Input
                            name='businessName'
                            id={'businessName'}
                            type='text'
                            $filled={true}
                            value={formData.businessName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.businessName ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>
                </div>

                {/* Line 3a - Tax Classification */}
                <div className='mb-6'>
                    <label className='block font-medium text-gray-700 mb-2'>
                        3.a Check the appropriate box for federal tax
                        classification of the entity/individual whose name is
                        entered on line 1. Check only one:
                    </label>
                    {errors.taxClassification && (
                        <ErrorMessageComponent>
                            {errors.taxClassification}
                        </ErrorMessageComponent>
                    )}
                    {[
                        'Individual/sole proprietor',
                        'C Corporation',
                        'S Corporation',
                        'Partnership',
                        'Trust/estate',
                        'Limited liability company',
                        'Other'
                    ].map(type => {
                        const safeId = type
                            .toLowerCase()
                            .replace(/[^a-z0-9]/g, '-')
                        return (
                            <div className='flex items-center mb-2' key={type}>
                                <input
                                    type='radio'
                                    id={safeId}
                                    name='taxClassification'
                                    value={type}
                                    checked={
                                        formData.taxClassification === type
                                    }
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className='h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500'
                                />
                                <label
                                    htmlFor={safeId}
                                    className='ml-2 inline-block text-gray-700'
                                >
                                    {type}
                                </label>
                            </div>
                        )
                    })}
                </div>

                <div className='mb-6'>
                    <InputContainer>
                        <InputLabel
                            $filled={true}
                            htmlFor={'limitedLiabilityType'}
                        >
                            {
                                'If you selected "Limited liability company", enter the tax classification (C=C corporation, S=S corporation, P=Partnership)'
                            }
                        </InputLabel>
                        <Input
                            name='limitedLiabilityType'
                            id={'limitedLiabilityType'}
                            type='text'
                            $filled={true}
                            value={formData.limitedLiabilityType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.limitedLiabilityType ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>
                </div>

                
                <div className='mb-6'>
                    <label className='block text-gray-700 mb-2'>
                        <span className='font-medium'>3b.</span> If on line 3a
                        you checked “Partnership” or “Trust/estate,” or checked
                        “LLC” and entered “P” as its tax classification, and you
                        are providing this form to a partnership, trust, or
                        estate in which you have an ownership interest, check
                        this box if you have any foreign partners, owners, or
                        beneficiaries.
                    </label>
                    <div className='flex items-center'>
                        <input
                            type='checkbox'
                            name='hasForeignPartners'
                            checked={formData.hasForeignPartners || false}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className='h-4 w-4 text-blue-600 border-gray-400 rounded focus:ring-blue-500'
                        />
                        <label
                            htmlFor='hasForeignPartners'
                            className='ml-2 text-gray-700'
                        >
                            Check Here
                        </label>
                    </div>
                </div>

                <div className='mb-6'>
                    <label className='block mb-2'>
                        <span className='font-medium'>4.</span> Exemptions
                        (codes apply only to certain entities, not individuals;
                        see instructions on page 3):
                    </label>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'exemptPayeeCode'}>
                            {'Exempt payee code (if any)'}
                        </InputLabel>
                        <Input
                            id={'exemptPayeeCode'}
                            name='exemptPayeeCode'
                            type='text'
                            $filled={true}
                            value={formData.exemptPayeeCode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.exemptPayeeCode ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>
                    <label className='block text-gray-700 mb-2'>
                        Exemption from Foreign Account Tax Compliance Act
                        (FATCA) reporting code (if any)
                    </label>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'exemptFromFatca'}>
                            {'FATCA exemption code (if any)'}
                        </InputLabel>
                        <Input
                            id={'exemptFromFatca'}
                            name='exemptFromFatca'
                            type='text'
                            $filled={true}
                            value={formData.exemptFromFatca || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.exemptFromFatca ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>
                </div>              
                <div className='mb-6'>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'streetAddress'}>
                            {
                                '5–6. Address (number, street, and apt. or suite no.)'
                            }
                        </InputLabel>
                        <Input
                            id={'streetAddress'}
                            name='streetAddress'
                            type='text'
                            value={formData.streetAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            $filled={true}
                        />
                        <ErrorMessageComponent>
                            {errors.streetAddress ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>

                    <div className='tax-form'>
                        <InputContainer className='col-5'>
                            <InputLabel $filled={true} htmlFor={'city'}>
                                {'City'}
                            </InputLabel>
                            <Input
                                name='city'
                                id={'city'}
                                type='text'
                                $filled={true}
                                value={formData.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessageComponent>
                                {errors.city ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                        <InputContainer className='col-5'>
                            <InputLabel $filled={true} htmlFor={'state'}>
                                {'State'}
                            </InputLabel>
                            <Input
                                name='state'
                                id={'state'}
                                type='text'
                                $filled={true}
                                value={formData.state}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessageComponent>
                                {errors.state ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                    </div>

                    <div className='mt-4'>
                        <InputContainer>
                            <InputLabel $filled={true} htmlFor={'zipCode'}>
                                {'Zip Code'}
                            </InputLabel>
                            <Input
                                name='zipCode'
                                id={'zipCode'}
                                type='text'
                                $filled={true}
                                value={formData.zipCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength={10}
                                pattern='\d{5}(-\d{4})?'
                            />
                            <ErrorMessageComponent>
                                {errors.zipCode ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                    </div>
                </div>

                <div className='mb-6'>
                    <label className='block font-medium text-gray-700 mb-2'>
                        {`Requester's name`}
                    </label>
                    <div className='tax-form'>
                        <InputContainer className='col-5'>
                            <InputLabel
                                $filled={true}
                                htmlFor={'requesterFirstName'}
                            >
                                {'Requester’s first name'}
                            </InputLabel>
                            <Input
                                name='requesterFirstName'
                                id={'requesterFirstName'}
                                type='text'
                                $filled={true}
                                value={formData.requesterFirstName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessageComponent>
                                {errors.requesterFirstName ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                        <InputContainer className='col-5'>
                            <InputLabel
                                $filled={true}
                                htmlFor={'requesterLastName'}
                            >
                                {'Requester’s last name'}
                            </InputLabel>
                            <Input
                                id={'requesterLastName'}
                                name='requesterLastName'
                                type='text'
                                $filled={true}
                                value={formData.requesterLastName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessageComponent>
                                {errors.requesterLastName ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                    </div>
                </div>

                <div className='mb-6'>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'requesterStreet'}>
                            {
                                'Requester’s address (number, street, and apt. or suite no.)'
                            }
                        </InputLabel>
                        <Input
                            id={'requesterStreet'}
                            name='requesterStreet'
                            type='text'
                            $filled={true}
                            value={formData.requesterStreet}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.requesterStreet ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>

                    <div className='tax-form'>
                        <InputContainer className='col-5'>
                            <InputLabel
                                $filled={true}
                                htmlFor={'requesterCity'}
                            >
                                {'Requester’s city'}
                            </InputLabel>
                            <Input
                                id={'requesterCity'}
                                name='requesterCity'
                                type='text'
                                value={formData.requesterCity}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                $filled={true}
                            />
                            <ErrorMessageComponent>
                                {errors.requesterCity ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                        <InputContainer className='col-5'>
                            <InputLabel
                                $filled={true}
                                htmlFor={'requesterState'}
                            >
                                {'Requester’s state'}
                            </InputLabel>
                            <Input
                                id={'requesterState'}
                                name='requesterState'
                                type='text'
                                $filled={true}
                                value={formData.requesterState}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessageComponent>
                                {errors.requesterState ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                    </div>

                    <div className='mt-4'>
                        <InputContainer>
                            <InputLabel $filled={true} htmlFor={'requesterZip'}>
                                {'Requester’s ZIP code'}
                            </InputLabel>
                            <Input
                                id={'requesterZip'}
                                name='requesterZip'
                                type='text'
                                $filled={true}
                                value={formData.requesterZip}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength={10}
                                pattern='\d{5}(-\d{4})?'
                            />
                            <ErrorMessageComponent>
                                {errors.requesterZip ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                    </div>
                </div>

                {/* Account Number (Optional) */}
                <div className='mb-6'>
                    <InputContainer>
                        <InputLabel $filled={true} htmlFor={'accountNumber'}>
                            {'7.List account number(s) here (optional)'}
                        </InputLabel>
                        <Input
                            id={'accountNumber'}
                            name='accountNumber'
                            type='text'
                            $filled={true}
                            value={formData.accountNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <ErrorMessageComponent>
                            {errors.accountNumber ?? ''}
                        </ErrorMessageComponent>
                    </InputContainer>
                </div>
                <div className='mb-10'>
                    <label className='mb-2 block'>
                        Part I - Taxpayer Identification Number (TIN)
                    </label>
                    <p>
                        Enter your TIN in the appropriate box. The TIN provided
                        must match the name given on line 1 to avoid backup
                        withholding. For individuals, this is generally your
                        social security number (SSN). However, for a resident
                        alien, sole proprietor, or disregarded entity, see the
                        instructions for Part I, later. For other entities, it
                        is your employer identification number (EIN). If you do
                        not have a number, see How to get a TIN, later.
                    </p>
                    <p className='mb-2'>
                        <strong>Note:</strong> If the account is in more than
                        one name, see the instructions for line 1. Also see{' '}
                        <em> What Name and Number To Give the Requester </em>{' '}
                        for guidelines on whose number to enter.
                    </p>

                    <div className='mb-4'>
                        <h3 className='mb-2'>Social Security Number</h3>
                        <div className='id-no mb-2'>
                            {[...Array(9)].map((_, i) => (
                                <input
                                    key={`ssn-${i}`}
                                    type='text'
                                    maxLength={1}
                                    pattern='\d*'
                                    className='form-cntrol'
                                    name={`ssn${i}`}
                                    value={formData[`ssn${i}`] || ''}
                                    onChange={e =>
                                        handleInputChange(e, i, 'ssn')
                                    }
                                    onKeyDown={e => handleKeyDown(e, i, 'ssn')} 
                                    onBlur={handleBlur}
                                    onFocus={e => e.target.select()}
                                    placeholder='-'
                                    ref={el => (ssnRefs.current[i] = el)}
                                />
                            ))}
                        </div>
                        <ErrorMessageComponent>
                            {errors.ssn0 ?? ''}
                        </ErrorMessageComponent>
                    </div>

                    <p className='mb-2'>OR</p>

                    <div>
                        <h3 className='mb-2'>Employer Identification Number</h3>
                        <div className='id-no mb-2'>
                            {[...Array(9)].map((_, i) => (
                                <input
                                    key={`ein-${i}`}
                                    type='text'
                                    maxLength={1}
                                    pattern='\d*'
                                    className='form-cntrol'
                                    name={`ein${i}`}
                                    value={formData[`ein${i}`] || ''}
                                    onChange={e =>
                                        handleInputChange(e, i, 'ein')
                                    }
                                    onKeyDown={e => handleKeyDown(e, i, 'ein')}  
                                    onBlur={handleBlur}
                                    onFocus={e => e.target.select()}
                                    placeholder='-'
                                    ref={el => (einRefs.current[i] = el)}
                                />
                            ))}
                        </div>
                        <ErrorMessageComponent>
                            {errors.ein0 ?? ''}
                        </ErrorMessageComponent>
                    </div>
                </div>

                <div className='mb-2'>
                    <label className='block mb-2'>
                        Part II - Certification
                    </label>
                    <div className='text-sm text-gray-800 space-y-3'>
                        <p>Under penalties of perjury, I certify that:</p>
                        <p>
                            1. The number shown on this form is my correct
                            taxpayer identification number (or I am waiting for
                            a number to be issued to me); and
                        </p>
                        <p>
                            2. I am not subject to backup withholding because:{' '}
                            <br />
                            (a) I am exempt from backup withholding, or <br />
                            (b) I have not been notified by the Internal Revenue
                            Service (IRS) that I am subject to backup
                            withholding as a result of a failure to report all
                            interest or dividends, or <br />
                            (c) the IRS has notified me that I am no longer
                            subject to backup withholding; and
                        </p>
                        <p>
                            3. I am a U.S. citizen or other U.S. person (defined
                            below); and
                        </p>
                        <p>
                            4. The FATCA code(s) entered on this form (if any)
                            indicating that I am exempt from FATCA reporting is
                            correct.
                        </p>

                        <p className='font-semibold'>
                            Certification instructions.
                        </p>
                        <p className='mb-2'>
                            You must cross out item 2 above if you have been
                            notified by the IRS that you are currently subject
                            to backup withholding because you have failed to
                            report all interest and dividends on your tax
                            return. For real estate transactions, item 2 does
                            not apply. For mortgage interest paid, acquisition
                            or abandonment of secured property, cancellation of
                            debt, contributions to an individual retirement
                            arrangement (IRA), and generally, payments other
                            than interest and dividends, you are not required to
                            sign the certification, but you must provide your
                            correct TIN. See the instructions for Part II,
                            later.
                        </p>
                    </div>
                </div>

                <div className='mb-2'>
                    <h4 className='mb-2'>Signature</h4>
                    <InputContainer className='col-5'>
                            <InputLabel
                                $filled={true}
                                htmlFor={'signature'}
                            >
                                {'Signature'}
                            </InputLabel>
                            <Input
                                id={'signature'}
                                name='signature'
                                type='text'
                                $filled={true}
                                value={formData.signature}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessageComponent>
                                {errors.signature ?? ''}
                            </ErrorMessageComponent>
                        </InputContainer>
                    {/* <div className='signature'>
                        <SignatureCanvas
                            penColor='black'
                            canvasProps={{
                                width: 600,
                                height: 150,
                                className: 'sigCanvas'
                            }}
                            ref={sigRef}
                            onEnd={handleEnd}
                        />
                        <ErrorMessageComponent>
                            {errors.signature ?? ''}
                        </ErrorMessageComponent>
                    </div>
                    <div className='clear-class'>
                        <Button
                            text='Clear'
                            color='black'
                            font='mont-14-normal'
                            width='13'
                            height='4'
                            clickFn={clearSignature}
                        />
                    </div> */}
                </div>

                <div className='mb-2'>
                    <InputLabel $filled={true} htmlFor={'date'}>
                        {' Date'}
                    </InputLabel>
                    <div style={{ flexGrow: 1, fontWeight: '600' }}>
                        <p>
                            {new Date().toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                            })}
                        </p>
                    </div>
                </div>

                <Button
                    text='Submit'
                    color='blue'
                    font='mont-14-normal'
                    width='13'
                    customClass={'m-auto'}
                    height='4'
                    clickFn={handleSubmit}
                />
            </form>
        </div>
    </div>
    )
}

export default W9

interface InputProps {
    $filled: boolean
}

const InputContainer = styled.div`
    position: relative;
    display: block;
    align-items: center;
    height: auto;
    background: ${colors.white89deg};
    box-sizing: border-box;
    box-shadow: ${colors.shadowBlack};
    border-radius: 2rem;
    margin: 1rem 0;
`

const InputLabel = styled.label<InputProps>`
    font-weight: 800;
    letter-spacing: 0.02em;
    font-size: 1.2rem;
    line-height: 1.5rem;
    font-weight: 500;
    color: #656565;
    pointer-events: none;
    transition: 0.2s all;
    transform: translateY(0);
    margin-bottom: 0.5rem;
    display: block;
`

const Input = styled.input<InputProps>`
    font-weight: 600;
    letter-spacing: 0.02em;
    font-size: 1.4rem;
    line-height: 1.7rem;
    text-indent: 1.8rem;
    height: 36px;
    width: 100%;
    border-radius: 2rem;
    border: 0.1rem solid #dddddd;
    transition:
        border 0.2s,
        padding 0.2s;
`

const ErrorMessageComponent = styled.div`
    position: absolute;
    right: 0;
    font-weight: 500;
    letter-spacing: 0.02em;
    font-size: 1.2rem;
    line-height: 0.5rem;
    color: ${colors.red};
    margin-right: 2rem;
`
